import { ToolItem } from '../types';

export const TOOLS_DIRECTORY: ToolItem[] = [
  // ==========================================
  // 1. EVERYDAY MONEY
  // ==========================================
  {
    id: 'percentage-calculator',
    slug: 'percentage-calculator',
    name: 'Percentage',
    label: 'Percentage',
    subtitle: 'Calculate x% of y',
    tagline: 'Instant percentage increase, decrease, proportion and base value calculations.',
    category: 'Everyday Money',
    description: 'Calculate percentages, proportions, percentage changes, and relative difference effortlessly.',
    iconName: 'Percent',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'pct', label: 'Percentage (%)', type: 'percentage', defaultValue: 15 },
      { id: 'baseVal', label: 'Of Value', type: 'money', defaultValue: 250 },
    ],
    defaultCalculation: {
      primaryLabel: 'Calculated Value',
      primaryUnit: '',
      secondaryLabels: [
        { label: 'Percentage of Value', formula: '15% of $250.00 = $37.50' },
        { label: 'Value + Percentage (Increase)', formula: '$250.00 + $37.50 = $287.50' },
        { label: 'Value - Percentage (Decrease)', formula: '$250.00 - $37.50 = $212.50' },
      ],
    },
    methodology: {
      source: 'Universal Financial Mathematics (P = B × r / 100)',
      formulaExplanation: 'Percentage calculation: Result = Base × (Rate / 100).',
      jurisdictionDate: 'Standard Mathematical Law',
      assumptions: ['Exact arithmetic precision rounded to 4 decimal places'],
    },
  },
  {
    id: 'discount-calculator',
    slug: 'discount-calculator',
    name: 'Discount',
    label: 'Discount',
    subtitle: 'Price after markdowns',
    tagline: 'Calculate sale savings, final checkout price, and double stacked discount coupons.',
    category: 'Everyday Money',
    description: 'Determine exact retail savings and final price after single or combined percentage discounts.',
    iconName: 'Tag',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'originalPrice', label: 'Original Price', type: 'money', defaultValue: 120 },
      { id: 'discountPercent', label: 'Discount (%)', type: 'percentage', defaultValue: 25 },
    ],
    defaultCalculation: {
      primaryLabel: 'Final Discounted Price',
      primaryUnit: '',
      secondaryLabels: [
        { label: 'You Save', formula: '$30.00 (25.0%)' },
        { label: 'Original List Price', formula: '$120.00' },
        { label: 'Effective Savings Ratio', formula: '1/4th of price reduced' },
      ],
    },
    methodology: {
      source: 'Standard Retail Pricing Principles',
      formulaExplanation: 'Discounted Price = Original Price × (1 - Discount / 100).',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Discount applied before local sales tax unless specified'],
    },
  },
  {
    id: 'margin-calculator',
    slug: 'margin-calculator',
    name: 'Margin',
    label: 'Margin',
    subtitle: 'Profit margin & markup',
    tagline: 'Calculate gross profit margin, revenue requirements, and cost of goods sold ratios.',
    category: 'Everyday Money',
    description: 'Analyze gross margin percentage and gross profit dollar amounts from item cost and selling price.',
    iconName: 'TrendingUp',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'cost', label: 'Cost of Goods ($)', type: 'money', defaultValue: 45 },
      { id: 'revenue', label: 'Revenue / Selling Price ($)', type: 'money', defaultValue: 75 },
    ],
    defaultCalculation: {
      primaryLabel: 'Gross Profit Margin',
      primaryUnit: '%',
      secondaryLabels: [
        { label: 'Gross Profit ($)', formula: '$30.00 per unit' },
        { label: 'Profit Margin', formula: '40.0%' },
        { label: 'Equivalent Markup', formula: '66.67% on cost' },
      ],
    },
    methodology: {
      source: 'GAAP & IFRS Accounting Frameworks',
      formulaExplanation: 'Gross Margin % = ((Revenue - Cost) / Revenue) × 100.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Direct variable unit costs excluded of operating overhead'],
    },
  },
  {
    id: 'markup-calculator',
    slug: 'markup-calculator',
    name: 'Markup',
    label: 'Markup',
    subtitle: 'Cost to selling price',
    tagline: 'Determine target selling price given cost and desired profit markup percentage.',
    category: 'Everyday Money',
    description: 'Calculate required selling price and target profit by applying commercial markup on cost.',
    iconName: 'DollarSign',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'unitCost', label: 'Unit Cost ($)', type: 'money', defaultValue: 50 },
      { id: 'markupPercent', label: 'Markup Target (%)', type: 'percentage', defaultValue: 50 },
    ],
    defaultCalculation: {
      primaryLabel: 'Required Selling Price',
      primaryUnit: '',
      secondaryLabels: [
        { label: 'Dollar Markup Added', formula: '$25.00' },
        { label: 'Final Retail Price', formula: '$75.00' },
        { label: 'Resulting Gross Margin', formula: '33.33%' },
      ],
    },
    methodology: {
      source: 'Commercial Pricing & Financial Math',
      formulaExplanation: 'Selling Price = Cost × (1 + Markup / 100).',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Standard commercial retail markup'],
    },
  },
  {
    id: 'tip-calculator',
    slug: 'tip-calculator',
    name: 'Tip',
    label: 'Tip',
    subtitle: 'Split bills easily',
    tagline: 'Calculate fair hospitality tips and gratuities across global dining customs.',
    category: 'Everyday Money',
    description: 'Calculate standard and custom gratuity amounts based on service quality and local tipping norms.',
    iconName: 'UtensilsCrossed',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'CA', 'GB', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global', 'US', 'CA', 'GB'],
    inputs: [
      { id: 'billAmount', label: 'Bill Amount', type: 'money', defaultValue: 85 },
      { id: 'tipPercent', label: 'Tip (%)', type: 'percentage', defaultValue: 18 },
      { id: 'splitCount', label: 'Split Among (People)', type: 'number', defaultValue: 2 },
    ],
    defaultCalculation: {
      primaryLabel: 'Total Bill with Tip',
      primaryUnit: '',
      secondaryLabels: [
        { label: 'Tip Amount', formula: '$15.30 (18%)' },
        { label: 'Each Person Pays', formula: '$50.15 / person' },
      ],
    },
    methodology: {
      source: 'Universal Hospitality Standards',
      formulaExplanation: 'Total = Bill × (1 + Tip/100) ; Per Person = Total / Split.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Standard pre-tax calculation baseline'],
    },
  },
  {
    id: 'split-bill-calculator',
    slug: 'split-bill-calculator',
    name: 'Split Bill',
    label: 'Split Bill',
    subtitle: 'Fair check division',
    tagline: 'Divide restaurant, grocery, and shared group expenses with uneven splits.',
    category: 'Everyday Money',
    description: 'Evenly or proportionally split complex group bills, tax, and service charges among friends.',
    iconName: 'Users',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'totalCheck', label: 'Total Check', type: 'money', defaultValue: 150 },
      { id: 'partySize', label: 'Number of Diners', type: 'number', defaultValue: 4 },
      { id: 'tipRate', label: 'Tip Rate (%)', type: 'percentage', defaultValue: 15 },
    ],
    defaultCalculation: {
      primaryLabel: 'Per-Person Share',
      primaryUnit: '/ person',
      secondaryLabels: [
        { label: 'Total Check with Gratuity', formula: '$172.50' },
        { label: 'Exact Split Share', formula: '$43.13 each' },
      ],
    },
    methodology: {
      source: 'Standard Expense Allocation Logic',
      formulaExplanation: 'Share = (Bill + Tip) / Diners.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Equal distribution among participants'],
    },
  },
  {
    id: 'vat-sales-tax',
    slug: 'vat-calculator',
    name: 'VAT/GST',
    label: 'VAT/GST',
    subtitle: 'Add or remove tax',
    tagline: 'Add or extract VAT, GST, and sales tax across 140+ countries with forward and reverse calculations.',
    category: 'Everyday Money',
    description: 'Instant VAT, TVA, GST, and Sales Tax calculation with one-click reverse tax calculations for businesses and consumers.',
    iconName: 'Receipt',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'FR', 'GB', 'DE', 'CA', 'AU', 'IN', 'CM'],
    regionsSupported: ['Global', 'FR', 'GB', 'DE', 'CA', 'AU', 'IN', 'CM'],
    inputs: [
      { id: 'taxAmount', label: 'Price Amount', type: 'money', defaultValue: 150 },
      { id: 'vatRate', label: 'Tax Rate (%)', type: 'percentage', defaultValue: 20.0 },
      { id: 'direction', label: 'Calculation Direction', type: 'select', defaultValue: 'add', options: [
        { label: 'Add Tax (Net → Gross TTC)', value: 'add' },
        { label: 'Extract Tax (Gross TTC → Net HT)', value: 'extract' },
      ]},
    ],
    defaultCalculation: {
      primaryLabel: 'Gross Total (incl. Tax)',
      secondaryLabels: [
        { label: 'Net Amount (excl. Tax)', formula: '$150.00' },
        { label: 'Tax Portion Paid', formula: '$30.00 (20%)' },
        { label: 'Total Billed', formula: '$180.00' },
      ],
    },
    methodology: {
      source: 'Official National Tax Codes (TVA, VAT, GST)',
      formulaExplanation: 'TTC = HT × (1 + Rate/100) ; HT = TTC / (1 + Rate/100).',
      jurisdictionDate: '2026 Standard Rates',
      assumptions: ['Standard headline rates without sector exemptions'],
    },
  },
  {
    id: 'work-hours-calculator',
    slug: 'work-hours-calculator',
    name: 'Work Hours',
    label: 'Work Hours',
    subtitle: 'Timesheet & overtime',
    tagline: 'Calculate total work hours, overtime rates, break deductions, and gross shift earnings.',
    category: 'Everyday Money',
    description: 'Accurately tally weekly shift hours, standard time, overtime multipliers, and gross shift wages.',
    iconName: 'Clock',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'regularHours', label: 'Regular Hours / Week', type: 'number', defaultValue: 40 },
      { id: 'hourlyRate', label: 'Hourly Rate ($)', type: 'money', defaultValue: 30 },
      { id: 'overtimeHours', label: 'Overtime Hours (1.5x)', type: 'number', defaultValue: 5 },
    ],
    defaultCalculation: {
      primaryLabel: 'Total Weekly Gross Pay',
      primaryUnit: '',
      secondaryLabels: [
        { label: 'Base Pay (40 hrs @ $30/hr)', formula: '$1,200.00' },
        { label: 'Overtime Pay (5 hrs @ $45/hr)', formula: '$225.00' },
        { label: 'Total Hours Worked', formula: '45.0 Hours' },
      ],
    },
    methodology: {
      source: 'Fair Labor Standards Act (FLSA) & EU Working Time Directive',
      formulaExplanation: 'Gross = (Regular Hours × Base Rate) + (Overtime Hours × 1.5 × Base Rate).',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Overtime calculated at standard time-and-a-half (1.5x)'],
    },
  },

  // ==========================================
  // 2. INCOME & TAX
  // ==========================================
  {
    id: 'salary-calculator',
    slug: 'salary-calculator',
    name: 'Salary',
    label: 'Salary',
    subtitle: 'Hourly to annual wages',
    tagline: 'Convert between hourly, weekly, monthly, and annual wages with precision net comparisons.',
    category: 'Income & Tax',
    description: 'Calculate your exact net salary per month and year across 50+ tax jurisdictions with progressive tax brackets.',
    iconName: 'Banknote',
    popular: true,
    featured: true,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['US', 'GB', 'CA', 'FR', 'DE', 'CH', 'AU', 'IN', 'SG', 'CM', 'Global'],
    regionsSupported: ['US', 'GB', 'CA', 'FR', 'DE', 'CH', 'AU', 'IN', 'SG', 'CM'],
    inputs: [
      { id: 'grossSalary', label: 'Gross Annual Salary', type: 'money', defaultValue: 75000 },
      { id: 'frequency', label: 'Payment Period', type: 'select', defaultValue: 'annual', options: [
        { label: 'Per Year (Annual)', value: 'annual' },
        { label: 'Per Month', value: 'monthly' },
        { label: 'Hourly Rate', value: 'hourly' },
      ]},
      { id: 'taxRegion', label: 'Tax Jurisdiction', type: 'region', defaultValue: 'US' },
    ],
    defaultCalculation: {
      primaryLabel: 'Net Monthly Take-Home',
      primaryUnit: '/ month',
      secondaryLabels: [
        { label: 'Annual Net Pay', formula: '$57,640 / year' },
        { label: 'Total Income Tax', formula: '$11,620 (15.5%)' },
        { label: 'Social / FICA / Pension', formula: '$5,740 (7.65%)' },
        { label: 'Effective Overall Tax Rate', formula: '23.15%' },
      ],
    },
    methodology: {
      source: 'IRS Publication 15-T (US), HMRC PAYE 2026/27 (UK), DGFiP (FR), CRA (CA)',
      formulaExplanation: 'Net = Gross - Standard Deduction Allowance - Σ(Progressive Tax Brackets) - Social Contributions.',
      jurisdictionDate: 'Certified 2026 Fiscal Year Brackets',
      assumptions: ['Single filer with standard deductions', 'No custom tax credits or child relief applied by default'],
    },
  },
  {
    id: 'gross-to-net-calculator',
    slug: 'gross-to-net-calculator',
    name: 'Gross to Net',
    label: 'Gross to Net',
    subtitle: 'Take-home pay',
    tagline: 'Calculate net paycheck from gross salary after social security, pension, and income tax tiers.',
    category: 'Income & Tax',
    description: 'Calculate net take-home pay after statutory withholdings, health insurance, and local deductions.',
    iconName: 'Calculator',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['US', 'GB', 'CA', 'FR', 'DE', 'CH', 'AU', 'Global'],
    regionsSupported: ['US', 'GB', 'CA', 'FR', 'DE'],
    inputs: [
      { id: 'grossAmount', label: 'Gross Salary', type: 'money', defaultValue: 65000 },
      { id: 'taxYear', label: 'Tax Year', type: 'select', defaultValue: '2026' },
    ],
    defaultCalculation: {
      primaryLabel: 'Estimated Net Take-Home',
      primaryUnit: '/ month',
      secondaryLabels: [
        { label: 'Net Monthly Pay', formula: '$4,120 / month' },
        { label: 'Federal & State Deductions', formula: '$1,296 / month' },
        { label: 'Net Retention Ratio', formula: '76.1%' },
      ],
    },
    methodology: {
      source: 'National Payroll Standards & Bracket Tables',
      formulaExplanation: 'Net = Gross - Statutory Withholdings - Marginal Brackets.',
      jurisdictionDate: '2026 Official Schedules',
      assumptions: ['Single earner standard statutory allowance'],
    },
  },
  {
    id: 'net-to-gross-calculator',
    slug: 'net-to-gross-calculator',
    name: 'Net to Gross',
    label: 'Net to Gross',
    subtitle: 'Required salary',
    tagline: 'Determine the exact gross salary offer needed to achieve your desired target take-home pay.',
    category: 'Income & Tax',
    description: 'Reverse calculate required gross compensation from your net financial lifestyle targets.',
    iconName: 'CreditCard',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['US', 'GB', 'CA', 'FR', 'DE', 'AU', 'Global'],
    regionsSupported: ['US', 'GB', 'CA', 'FR', 'DE'],
    inputs: [
      { id: 'desiredNet', label: 'Desired Monthly Net Pay', type: 'money', defaultValue: 5000 },
    ],
    defaultCalculation: {
      primaryLabel: 'Required Gross Salary',
      primaryUnit: '/ year',
      secondaryLabels: [
        { label: 'Target Gross Annual', formula: '$82,450 / year' },
        { label: 'Gross Monthly Equivalent', formula: '$6,870 / month' },
        { label: 'Estimated Taxes Paid', formula: '$22,450 / year' },
      ],
    },
    methodology: {
      source: 'Reverse Tax Solver Iteration Model',
      formulaExplanation: 'Iterative binary root solver over bracket tiers until f(Gross) - Tax(Gross) = Net.',
      jurisdictionDate: '2026 Fiscal Brackets',
      assumptions: ['Standard statutory bracket deduction profile'],
    },
  },
  {
    id: 'income-tax-calculator',
    slug: 'income-tax-calculator',
    name: 'Income Tax',
    label: 'Income Tax',
    subtitle: 'Tax bracket estimator',
    tagline: 'Marginal vs. effective tax rate decomposition with progressive tier charts.',
    category: 'Income & Tax',
    description: 'Visualize how your income moves through progressive tax bands and where every tax dollar is allocated.',
    iconName: 'FileText',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['US', 'GB', 'CA', 'FR', 'DE', 'AU', 'Global'],
    regionsSupported: ['US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    inputs: [
      { id: 'taxableIncome', label: 'Taxable Income', type: 'money', defaultValue: 100000 },
      { id: 'taxRegion', label: 'Country', type: 'region', defaultValue: 'US' },
    ],
    defaultCalculation: {
      primaryLabel: 'Total Income Tax Due',
      secondaryLabels: [
        { label: 'Marginal Tax Rate', formula: '22.0%' },
        { label: 'Effective Average Rate', formula: '14.26%' },
        { label: 'Remaining Disposable Income', formula: '$85,740' },
      ],
    },
    methodology: {
      source: 'National Revenue Authorities (IRS, HMRC, BMF, ATO)',
      formulaExplanation: 'Marginal rate applies strictly to income within the highest bracket threshold.',
      jurisdictionDate: '2026 Tax Schedules',
      assumptions: ['Federal baseline excluding local/municipal surcharges unless explicitly selected'],
    },
  },
  {
    id: 'employer-cost-calculator',
    slug: 'employer-cost-calculator',
    name: 'Employer Cost',
    label: 'Employer Cost',
    subtitle: 'Total employment cost',
    tagline: 'Calculate employer payroll contributions, social charges, insurance, and full cost-to-company.',
    category: 'Income & Tax',
    description: 'Determine the total superannuation, employer payroll tax, social contributions, and total loaded employee cost.',
    iconName: 'Building2',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['US', 'GB', 'CA', 'FR', 'DE', 'AU', 'Global'],
    regionsSupported: ['US', 'GB', 'CA', 'FR', 'DE'],
    inputs: [
      { id: 'baseSalary', label: 'Employee Base Salary', type: 'money', defaultValue: 80000 },
      { id: 'benefitsPct', label: 'Benefits & Social Taxes (%)', type: 'percentage', defaultValue: 18.5 },
    ],
    defaultCalculation: {
      primaryLabel: 'Total Cost to Company (CTC)',
      primaryUnit: '/ year',
      secondaryLabels: [
        { label: 'Total Employer Taxes & Benefits', formula: '$14,800 / year' },
        { label: 'Total Annual Employer Outlay', formula: '$94,800 / year' },
        { label: 'Multiplier on Base', formula: '1.185x' },
      ],
    },
    methodology: {
      source: 'National Labor & Employer Social Contribution Frameworks',
      formulaExplanation: 'CTC = Gross Base + Employer FICA/NIC/Social Security + Mandated Benefits.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Standard mandatory social package'],
    },
  },

  // ==========================================
  // 3. CURRENCY & TRANSFERS
  // ==========================================
  {
    id: 'currency-converter',
    slug: 'currency-converter',
    name: 'Currency Converter',
    label: 'Currency Converter',
    subtitle: 'Real-time conversions',
    tagline: '170+ currencies at verified mid-market rates without hidden bank markups.',
    category: 'Currency & Transfers',
    description: 'Instant multi-currency exchange calculations using institutional mid-market rates with transparent spread analysis.',
    iconName: 'RefreshCw',
    popular: true,
    featured: true,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'EU', 'GB', 'CA', 'CH', 'JP', 'AU', 'IN', 'AFRICA'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'amount', label: 'Amount to convert', type: 'money', defaultValue: 1000 },
      { id: 'fromCurrency', label: 'From Currency', type: 'select', defaultValue: 'USD' },
      { id: 'toCurrency', label: 'To Currency', type: 'select', defaultValue: 'EUR' },
    ],
    defaultCalculation: {
      primaryLabel: 'Converted Amount',
      primaryUnit: 'EUR',
      secondaryLabels: [
        { label: 'Mid-Market Rate', formula: '1 USD = 0.92340 EUR' },
        { label: 'Inverse Rate', formula: '1 EUR = 1.08295 USD' },
        { label: 'Estimated Bank Spread Savings', formula: '~ $28.50 vs traditional retail markup (3%)' },
      ],
    },
    methodology: {
      source: 'ECB, BIS & Institutional FX Interbank Feeds',
      formulaExplanation: 'Converted = Amount × (Rate_To / Rate_From). Raw mid-market interbank rate without any retail markup.',
      jurisdictionDate: 'Updated live: Aug 2026',
      assumptions: ['Mid-market rates represent wholesale exchange pricing', 'Retail banks often add 1.5% - 4.0% hidden markups'],
    },
  },
  {
    id: 'exchange-rates-matrix',
    slug: 'exchange-rates',
    name: 'Exchange Rates',
    label: 'Exchange Rates',
    subtitle: 'Historical data trends',
    tagline: 'Global cross-rates matrix and historical volatility comparisons.',
    category: 'Currency & Transfers',
    description: 'Track real-time cross-currency rates across all major reserve and regional currencies in one synchronized grid.',
    iconName: 'TrendingUp',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'baseCurrency', label: 'Base Currency', type: 'select', defaultValue: 'USD' },
    ],
    defaultCalculation: {
      primaryLabel: 'Base: 1.00 USD',
      secondaryLabels: [
        { label: 'EUR', formula: '0.9234 (+0.04%)' },
        { label: 'GBP', formula: '0.7911 (-0.12%)' },
        { label: 'JPY', formula: '149.82 (+0.21%)' },
        { label: 'CHF', formula: '0.8845 (+0.01%)' },
      ],
    },
    methodology: {
      source: 'Global Central Bank Composite Feeds',
      formulaExplanation: 'Direct cross-rate matrix based on USD triangular arbitrage equilibrium.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Continuous 24/5 liquidity window matching London and NY trading sessions'],
    },
  },
  {
    id: 'historical-fx',
    slug: 'historical-fx',
    name: 'Historical FX',
    label: 'Historical FX',
    subtitle: 'Rates over time',
    tagline: 'Analyze 30-day, 1-year, and 10-year foreign exchange trends and historical high/low ranges.',
    category: 'Currency & Transfers',
    description: 'Examine historical exchange rate series, moving averages, standard deviation, and currency purchasing trends.',
    iconName: 'LineChart',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'pair', label: 'Currency Pair', type: 'select', defaultValue: 'USD/EUR' },
      { id: 'range', label: 'Timeframe', type: 'select', defaultValue: '1Y' },
    ],
    defaultCalculation: {
      primaryLabel: '52-Week Range',
      primaryUnit: '',
      secondaryLabels: [
        { label: '52-Week High', formula: '0.9412 EUR' },
        { label: '52-Week Low', formula: '0.8985 EUR' },
        { label: '1-Year Net Trend', formula: '+1.45% USD Strength' },
      ],
    },
    methodology: {
      source: 'BIS & Historical Central Bank Fixing Series',
      formulaExplanation: 'Historical daily London 16:00 close fixing rates.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['End of day official fixing benchmarks'],
    },
  },
  {
    id: 'compare-transfers',
    slug: 'transfer-comparison',
    name: 'Transfer Comparison',
    label: 'Transfer Comparison',
    subtitle: 'Find lowest fees',
    tagline: 'Uncover hidden FX markup margins, fixed transfer fees & delivery speeds across providers.',
    category: 'Currency & Transfers',
    description: 'Compare transparent cost breakdowns of sending money internationally across Wise, OFX, Remitly, Western Union, and top banks.',
    iconName: 'ArrowRightLeft',
    popular: true,
    featured: true,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'EU', 'CA', 'AU', 'IN', 'PH', 'NG', 'MX'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'sendAmount', label: 'You Send', type: 'money', defaultValue: 1000 },
      { id: 'sendCurrency', label: 'From Currency', type: 'select', defaultValue: 'USD' },
      { id: 'receiveCurrency', label: 'Recipient Gets', type: 'select', defaultValue: 'INR' },
    ],
    defaultCalculation: {
      primaryLabel: 'Recipient Gets (Best Provider)',
      primaryUnit: 'INR',
      secondaryLabels: [
        { label: 'Best Total Cost', formula: 'Wise (Fee: $5.20 | Rate: 83.10 INR)' },
        { label: 'Traditional Bank (Avg)', formula: 'Fee: $25.00 + 3.2% spread (Recipient gets ₹79,850)' },
        { label: 'Total Saved with Smart Provider', formula: '₹3,250 (~ $39.00 USD)' },
      ],
    },
    methodology: {
      source: 'Real-Time Provider API Quotes & Mid-Market Reference Rates',
      formulaExplanation: 'Total Cost = Upfront Transfer Fee + (Mid-Market Rate - Offered FX Rate) × Send Amount.',
      jurisdictionDate: 'Verified Hourly Index',
      assumptions: ['Bank account-to-bank account electronic transfers', 'Standard delivery speed within 24-48 hours'],
    },
  },

  // ==========================================
  // 4. BORROW
  // ==========================================
  {
    id: 'loan-calculator',
    slug: 'loan-calculator',
    name: 'Loan',
    label: 'Loan',
    subtitle: 'Monthly installments',
    tagline: 'Personal, auto, and student loan payment calculator with full total interest schedule.',
    category: 'Borrow',
    description: 'Calculate monthly fixed installments, total lifetime finance charges, and payoff timelines for personal and installment loans.',
    iconName: 'CreditCard',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'loanAmount', label: 'Loan Amount ($)', type: 'money', defaultValue: 25000 },
      { id: 'loanTermMonths', label: 'Term (Months)', type: 'number', defaultValue: 48 },
      { id: 'interestRate', label: 'Annual Interest Rate (%)', type: 'percentage', defaultValue: 7.2 },
    ],
    defaultCalculation: {
      primaryLabel: 'Monthly Payment',
      primaryUnit: '/ month',
      secondaryLabels: [
        { label: 'Principal Borrowed', formula: '$25,000.00' },
        { label: 'Total Interest Charge', formula: '$3,878.50' },
        { label: 'Total Repayment Amount', formula: '$28,878.50' },
      ],
    },
    methodology: {
      source: 'Standard Actuarial Installment Formula',
      formulaExplanation: 'PMT = P × (r(1+r)^n) / ((1+r)^n - 1).',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Fixed interest rate throughout repayment term'],
    },
  },
  {
    id: 'mortgage-calculator',
    slug: 'mortgage-calculator',
    name: 'Mortgage',
    label: 'Mortgage',
    subtitle: 'Home loan planner',
    tagline: 'Comprehensive principal, interest, amortization schedule & payoff projections.',
    category: 'Borrow',
    description: 'Calculate your exact monthly mortgage payment with breakdown of principal, interest, taxes, and total lifetime borrowing cost.',
    iconName: 'Home',
    popular: true,
    featured: true,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    inputs: [
      { id: 'homePrice', label: 'Home Price / Loan Amount', type: 'money', defaultValue: 350000 },
      { id: 'downPayment', label: 'Down Payment ($ or %)', type: 'money', defaultValue: 70000 },
      { id: 'interestRate', label: 'Annual Interest Rate (%)', type: 'percentage', defaultValue: 5.85 },
      { id: 'loanTermYears', label: 'Loan Term (Years)', type: 'number', defaultValue: 25 },
    ],
    defaultCalculation: {
      primaryLabel: 'Monthly Payment (P&I)',
      primaryUnit: '/ month',
      secondaryLabels: [
        { label: 'Loan Principal Borrowed', formula: '$280,000' },
        { label: 'Total Interest Over 25 Years', formula: '$253,892' },
        { label: 'Total Overall Amount Paid', formula: '$533,892' },
        { label: 'Payoff Date', formula: 'August 2051' },
      ],
    },
    methodology: {
      source: 'Standard Actuarial Amortization Formula (M = P [ i(1 + i)^n ] / [ (1 + i)^n – 1])',
      formulaExplanation: 'M = Monthly payment, P = Principal loan balance, i = Monthly interest rate (APR / 12), n = Total number of monthly payments.',
      jurisdictionDate: 'Standard Global Amortization Rules',
      assumptions: ['Fixed interest rate for entire duration', 'Monthly compounding standard'],
    },
  },
  {
    id: 'apr-calculator',
    slug: 'apr-calculator',
    name: 'APR',
    label: 'APR',
    subtitle: 'True cost of borrowing',
    tagline: 'Reveal the true Annual Percentage Rate including origination points, broker fees, and closing costs.',
    category: 'Borrow',
    description: 'Calculate effective annual percentage rate (APR) accounting for upfront lender points and administration fees.',
    iconName: 'Percent',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'AU'],
    regionsSupported: ['Global', 'US', 'GB'],
    inputs: [
      { id: 'principal', label: 'Loan Principal', type: 'money', defaultValue: 100000 },
      { id: 'statedRate', label: 'Stated Note Rate (%)', type: 'percentage', defaultValue: 6.0 },
      { id: 'closingFees', label: 'Upfront Fees & Points ($)', type: 'money', defaultValue: 2500 },
      { id: 'termMonths', label: 'Term (Months)', type: 'number', defaultValue: 120 },
    ],
    defaultCalculation: {
      primaryLabel: 'Effective APR',
      primaryUnit: '%',
      secondaryLabels: [
        { label: 'Stated Nominal Rate', formula: '6.00%' },
        { label: 'True Effective APR', formula: '6.54% (includes $2,500 fees)' },
        { label: 'Net Financed Amount', formula: '$97,500' },
      ],
    },
    methodology: {
      source: 'Truth in Lending Act (Regulation Z) & Consumer Credit Directive',
      formulaExplanation: 'Internal Rate of Return (IRR) solving for discount rate equating net proceeds to cash flow stream.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Fees financed or paid at loan inception'],
    },
  },
  {
    id: 'amortization-calculator',
    slug: 'amortization-calculator',
    name: 'Amortization',
    label: 'Amortization',
    subtitle: 'Full payoff schedule',
    tagline: 'View detailed month-by-month and annual principal vs interest amortization schedules.',
    category: 'Borrow',
    description: 'Inspect precise breakdown of how each payment progressively shifts from interest servicing to principal equity.',
    iconName: 'Calendar',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'balance', label: 'Loan Balance', type: 'money', defaultValue: 200000 },
      { id: 'rate', label: 'Interest Rate (%)', type: 'percentage', defaultValue: 6.25 },
      { id: 'years', label: 'Duration (Years)', type: 'number', defaultValue: 30 },
    ],
    defaultCalculation: {
      primaryLabel: 'Monthly Payment',
      primaryUnit: '/ month',
      secondaryLabels: [
        { label: 'Year 1 Interest Paid', formula: '$12,410' },
        { label: 'Year 1 Principal Paid', formula: '$2,360' },
        { label: 'Equity Crossover Point', formula: 'Year 16' },
      ],
    },
    methodology: {
      source: 'Standard Loan Amortization Mathematics',
      formulaExplanation: 'Interest_t = Balance_(t-1) × (r/12) ; Principal_t = PMT - Interest_t.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Equal monthly payments on standard amortization schedule'],
    },
  },
  {
    id: 'early-repayment-calculator',
    slug: 'extra-payments-calculator',
    name: 'Extra Payments',
    label: 'Extra Payments',
    subtitle: 'Pay off debt faster',
    tagline: 'Calculate years shaved off your mortgage and tens of thousands saved in interest.',
    category: 'Borrow',
    description: 'See the exact impact of making an extra lump-sum or recurring monthly principal contribution.',
    iconName: 'TrendingDown',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'balance', label: 'Current Remaining Balance', type: 'money', defaultValue: 250000 },
      { id: 'extraPayment', label: 'Extra Monthly Payment', type: 'money', defaultValue: 200 },
      { id: 'rate', label: 'Interest Rate (%)', type: 'percentage', defaultValue: 6.0 },
    ],
    defaultCalculation: {
      primaryLabel: 'Total Interest Saved',
      secondaryLabels: [
        { label: 'Time Shortened', formula: '4 Years and 8 Months earlier' },
        { label: 'Total Interest Saved', formula: '$38,420' },
      ],
    },
    methodology: {
      source: 'Actuarial Accelerated Amortization Engine',
      formulaExplanation: 'Extra payments reduce outstanding principal balance directly, eliminating future compounding on that portion.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['No lender prepayment penalty fees applicable'],
    },
  },

  // ==========================================
  // 5. SAVE & INVEST
  // ==========================================
  {
    id: 'compound-interest-calculator',
    slug: 'compound-interest',
    name: 'Compound Interest',
    label: 'Compound Interest',
    subtitle: 'Growth over time',
    tagline: 'Model exponential compound growth with monthly deposits and inflation adjustment.',
    category: 'Save & Invest',
    description: 'Project how your initial capital and monthly savings compound over 5 to 40 years with customizable compounding frequencies.',
    iconName: 'PiggyBank',
    popular: true,
    featured: true,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'initialDeposit', label: 'Initial Investment', type: 'money', defaultValue: 10000 },
      { id: 'monthlyContribution', label: 'Monthly Contribution', type: 'money', defaultValue: 500 },
      { id: 'estimatedReturn', label: 'Estimated Annual Return (%)', type: 'percentage', defaultValue: 7.5 },
      { id: 'years', label: 'Investment Horizon (Years)', type: 'number', defaultValue: 20 },
    ],
    defaultCalculation: {
      primaryLabel: 'Future Portfolio Value',
      secondaryLabels: [
        { label: 'Total Contributions', formula: '$130,000 (Initial $10k + $120k deposits)' },
        { label: 'Total Compound Interest Earned', formula: '$189,450' },
        { label: 'Interest Represents', formula: '59.3% of Total Final Wealth' },
      ],
    },
    methodology: {
      source: 'Standard Compound Growth Mathematics A = P(1 + r/n)^(nt) + PMT × [((1 + r/n)^(nt) - 1) / (r/n)]',
      formulaExplanation: 'Calculates periodic compound compounding plus future value of an ordinary monthly annuity.',
      jurisdictionDate: 'Standard Financial Math',
      assumptions: ['Constant annual rate of return', 'Contributions made at end of each period'],
    },
  },
  {
    id: 'savings-calculator',
    slug: 'savings-calculator',
    name: 'Savings',
    label: 'Savings',
    subtitle: 'Target savings plan',
    tagline: 'Determine how much to save monthly to reach your home deposit, wedding, or emergency fund goal.',
    category: 'Save & Invest',
    description: 'Calculate target monthly savings deposits and timeline to achieve specific financial milestone amounts.',
    iconName: 'Wallet',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'targetGoal', label: 'Savings Target ($)', type: 'money', defaultValue: 50000 },
      { id: 'startingBalance', label: 'Starting Balance ($)', type: 'money', defaultValue: 5000 },
      { id: 'targetMonths', label: 'Timeframe (Months)', type: 'number', defaultValue: 36 },
      { id: 'savingsYield', label: 'Annual APY Yield (%)', type: 'percentage', defaultValue: 4.5 },
    ],
    defaultCalculation: {
      primaryLabel: 'Required Monthly Deposit',
      primaryUnit: '/ month',
      secondaryLabels: [
        { label: 'Target Goal Amount', formula: '$50,000 in 3 years' },
        { label: 'Total Principal Saved', formula: '$46,200' },
        { label: 'Interest Yield Earned', formula: '$3,800' },
      ],
    },
    methodology: {
      source: 'Sinking Fund Annuity Solver',
      formulaExplanation: 'PMT = (Goal - FV(Starting)) / [ ((1+r)^n - 1) / r ].',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Constant APY yield across high-yield savings account'],
    },
  },
  {
    id: 'investment-return-calculator',
    slug: 'investment-return-calculator',
    name: 'Investment Return',
    label: 'Investment Return',
    subtitle: 'ROI & portfolio yield',
    tagline: 'Calculate Return on Investment (ROI), Annualized Return (CAGR), and real capital gains.',
    category: 'Save & Invest',
    description: 'Evaluate true investment performance across stocks, ETFs, mutual funds, and real estate assets.',
    iconName: 'TrendingUp',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'initialCapital', label: 'Initial Capital ($)', type: 'money', defaultValue: 25000 },
      { id: 'endCapital', label: 'Ending Value ($)', type: 'money', defaultValue: 62000 },
      { id: 'holdYears', label: 'Holding Period (Years)', type: 'number', defaultValue: 7 },
    ],
    defaultCalculation: {
      primaryLabel: 'Annualized Return (CAGR)',
      primaryUnit: '% / yr',
      secondaryLabels: [
        { label: 'Total ROI', formula: '+148.0% total gain' },
        { label: 'Total Profit', formula: '+$37,000.00' },
        { label: 'CAGR (Annualized)', formula: '13.86% per year' },
      ],
    },
    methodology: {
      source: 'CFA Institute Performance Attribution Standards',
      formulaExplanation: 'CAGR = (Ending / Beginning)^(1 / Years) - 1 ; ROI = (Gain / Initial) × 100.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Reinvested dividends without ongoing intermediate cash injections'],
    },
  },
  {
    id: 'inflation-purchasing-power',
    slug: 'inflation-calculator',
    name: 'Inflation',
    label: 'Inflation',
    subtitle: 'Purchasing power',
    tagline: 'Discover what your money will truly be worth after 5, 10, or 20 years of inflation.',
    category: 'Save & Invest',
    description: 'Understand how consumer price index (CPI) trends erode purchasing power over time.',
    iconName: 'TrendingUp',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'EU', 'CA', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'currentAmount', label: 'Current Amount', type: 'money', defaultValue: 50000 },
      { id: 'inflationRate', label: 'Average Inflation Rate (%)', type: 'percentage', defaultValue: 3.2 },
      { id: 'yearsAhead', label: 'Years Ahead', type: 'number', defaultValue: 15 },
    ],
    defaultCalculation: {
      primaryLabel: 'Future Equivalent Purchasing Power',
      secondaryLabels: [
        { label: 'Real Purchasing Loss', formula: '-37.8% drop in actual buying capacity' },
        { label: 'Amount needed in 15 yrs to match $50k today', formula: '$80,410' },
      ],
    },
    methodology: {
      source: 'CPI Historical Models & Fisher Equation',
      formulaExplanation: 'Future Required = P × (1 + i)^t ; Real Value = P / (1 + i)^t.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Steady compounding rate of price inflation'],
    },
  },
  {
    id: 'retirement-calculator',
    slug: 'retirement-calculator',
    name: 'Retirement',
    label: 'Retirement',
    subtitle: 'Future nest egg',
    tagline: 'Estimate retirement savings required to replace pre-retirement income and maintain lifestyle.',
    category: 'Save & Invest',
    description: 'Plan for pension, 401(k), IRA, superannuation, and life annuity sustainability across retirement.',
    iconName: 'UserCheck',
    popular: true,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'FR', 'DE', 'AU'],
    regionsSupported: ['Global', 'US', 'GB', 'CA'],
    inputs: [
      { id: 'currentAge', label: 'Current Age', type: 'number', defaultValue: 32 },
      { id: 'retireAge', label: 'Retirement Age', type: 'number', defaultValue: 65 },
      { id: 'currentSavings', label: 'Current Nest Egg ($)', type: 'money', defaultValue: 45000 },
      { id: 'annualSavings', label: 'Annual Contributions ($)', type: 'money', defaultValue: 12000 },
    ],
    defaultCalculation: {
      primaryLabel: 'Projected Retirement Nest Egg',
      primaryUnit: '',
      secondaryLabels: [
        { label: 'Projected Total at Age 65', formula: '$1,842,500' },
        { label: 'Safe Annual Income (4% Rule)', formula: '$73,700 / year' },
        { label: 'Estimated Monthly Pension', formula: '$6,141 / month' },
      ],
    },
    methodology: {
      source: 'Actuarial Life-Cycle Financial Planning & Trinity Study',
      formulaExplanation: 'FV accumulation over working years + sustainable safe withdrawal rate distribution.',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['7% nominal pre-retirement return, 4% safe withdrawal rate'],
    },
  },
  {
    id: 'fire-calculator',
    slug: 'fire-calculator',
    name: 'FIRE',
    label: 'FIRE',
    subtitle: 'Financial independence',
    tagline: 'Calculate your Financial Independence / Retire Early number, savings rate, and target date.',
    category: 'Save & Invest',
    description: 'Determine exact FIRE target multiplier (25x - 33x expenses) and years required to reach full financial autonomy.',
    iconName: 'Flame',
    popular: false,
    featured: false,
    status: 'PUBLISHED',
    availability: 'AVAILABLE',
    marketSupport: ['Global', 'US', 'GB', 'CA', 'EU', 'AU'],
    regionsSupported: ['Global'],
    inputs: [
      { id: 'annualSpending', label: 'Annual Living Expenses ($)', type: 'money', defaultValue: 48000 },
      { id: 'currentNetWorth', label: 'Invested Assets ($)', type: 'money', defaultValue: 120000 },
      { id: 'annualSavings', label: 'Annual Savings Added ($)', type: 'money', defaultValue: 30000 },
      { id: 'swrPct', label: 'Safe Withdrawal Rate (%)', type: 'percentage', defaultValue: 3.75 },
    ],
    defaultCalculation: {
      primaryLabel: 'Your FIRE Number Target',
      primaryUnit: '',
      secondaryLabels: [
        { label: 'Target Portfolio (26.7x Expenses)', formula: '$1,280,000' },
        { label: 'Years to Financial Independence', formula: '14.2 Years' },
        { label: 'Current FIRE Progress', formula: '9.4% achieved' },
      ],
    },
    methodology: {
      source: 'Trinity Study & Modern FIRE Mathematical Models',
      formulaExplanation: 'FIRE Target = Annual Expenses / (SWR / 100). Time = ln((Target × r + S) / (NW × r + S)) / ln(1 + r).',
      jurisdictionDate: 'Aug 2026',
      assumptions: ['Constant annual savings and steady inflation-adjusted investment returns'],
    },
  },
];
